import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Box, Typography } from "@mui/material";
import { Button } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CheckboxPopover from "../../../../../ts/design/checkbox-popover";
import ColumnSelector from "../../../../../ts/design/column-selector";
import DateRangePicker from "../../../../../ts/design/date-range-picker";
import Searchbar from "../../../../../ts/design/search-bar";
import TimeFilterSelect from "../../../../design/time-filter-select";
import { hasPermission } from "../../../../utils/helpers";
import { RequestStatusTypes } from "../../store/worker-requests-types";

interface WorkerRequestHeaderProps {
	search?: undefined | ((value: string) => void);
	value: string | undefined;
	columns?: any[]; // Replace with the actual type of your columns
	statusOptions?: RequestStatusTypes[];
	setStatusOptions?: (options: RequestStatusTypes[]) => void;
	handleSelectColumns?: (checked: boolean, field: string) => void;
	// onTimeFilterChange?: (event: SelectChangeEvent<string>) => void;
	setTimeFilterOption?: any;
	timeFilterOption?: any;
	onRaiseRequestClick?: () => void;
	// When true (Time Off Requests page), the header shows a date range picker
	// and relies on the shared AppliedFilters bar for status/location/work
	// center/shift pattern instead of the legacy status popover + time dropdown.
	useSharedFilters?: boolean;
	dateRange?: [string, string];
	setDateRange?: (value: [string, string]) => void;
	rangeError?: string;
	maxRangeDays?: number;
}

const WorkerRequestHeader: React.FC<WorkerRequestHeaderProps> = ({
	search,
	value,
	columns,
	handleSelectColumns,
	statusOptions = [],
	setStatusOptions = () => {
		("");
	},
	setTimeFilterOption,
	timeFilterOption,
	onRaiseRequestClick,
	useSharedFilters = false,
	dateRange,
	setDateRange,
	rangeError,
	maxRangeDays,
}) => {
  const { t } = useTranslation();
  const userRolesWithPermission = useSelector(
    (state: any) => state.user.userData.userRolesWithPermissions
  );
	const [anchorElement, setAnchorElement] = useState(null);
	const timeFilterOptions = [
		{ label: t("worker-requests_last-30-days"), value: "last_30_days" },
		{ label: t("worker-requests_last-90-days"), value: "last_90_days" },
		{ label: t("worker-requests_this-year"), value: "this_year" },
		{ label: t("worker-requests_all-time"), value: "till_today" },
	];

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElement(event.currentTarget as any);
	};

	const handleClose = () => {
		setAnchorElement(null);
	};

	const getStatusText = () => {
		const selectedOptions = statusOptions.filter((option) => option.checked);
		if (selectedOptions.length === 1) {
			return selectedOptions[0].label;
		} else if (selectedOptions.length === statusOptions.length) {
			return t("worker-requests_all-statuses");
		} else if (selectedOptions.length > 1) {
			return t("worker-requests_status-count", {
				count: selectedOptions.length,
			});
		}
		return t("worker-requests_none");
	};
	// console.log("columns", columns);
	return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={3}
      py={1.2}
    >
      <Box display="flex" alignItems={"center"}>
        <ColumnSelector
          columns={columns}
          handleSelectColumns={handleSelectColumns}
        />
        <Searchbar
          placeholder={""}
          width={"35ch"}
          variant={"filled"}
          setSearch={(value) => search && search(value)}
          value={value}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {useSharedFilters ? (
          dateRange &&
          setDateRange && (
            <Box sx={{ flexShrink: 0 }}>
              <DateRangePicker
                value={dateRange}
                setValue={setDateRange}
                variant="filled"
                size="small"
                disableFuture
                maxRangeDays={maxRangeDays}
                errorMessage={rangeError}
              />
            </Box>
          )
        ) : (
          <>
            <Box>
              <Box
                onClick={handleClick}
                sx={{
                  padding: "12px",
                  background: "#f8f8f8",
                  borderRadius: "4px",
                  display: "flex",
                  gap: "4px",
                  cursor: "pointer",
                  minWidth: "150px",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body1" component="span">
                  {getStatusText()}
                </Typography>
                {anchorElement ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </Box>
              <CheckboxPopover
                options={statusOptions}
                anchorElement={anchorElement}
                onClose={handleClose}
                setOptions={setStatusOptions as any}
                minWidth={"200px"}
                disableMaxHeight
              />
            </Box>
            <TimeFilterSelect
              value={timeFilterOption}
              onChange={(event) => setTimeFilterOption(event.target.value)}
              options={timeFilterOptions}
            />
          </>
        )}
        {hasPermission(
          userRolesWithPermission,
          "WORKER_REQUESTS.RAISE_WORKER_REQUEST"
        ) && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#2F4D8B",
              "&:hover": {
                backgroundColor: "#1D325F",
              },
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "24px",
              letterSpacing: "0.4px",
              height: "40px",
              borderRadius: "4px",
              px: 2,
            }}
            onClick={onRaiseRequestClick}
          >
            {t("worker-requests_mark-time-off")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WorkerRequestHeader;
